import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { getIsMobileOrTablet } from '../../utils/device.util';

export interface StatsCardData {
  title: string;
  value: string | number;
  color: string;
  valueColor?: string; // New property for value-specific color
  change?: string;
  changeType?: 'positive' | 'negative' | 'neutral';
  icon?: string;
  iconClass?: string;
  iconStyle?: any;
}

/**
 * Lightweight, reusable statistics card component for displaying a title, primary value, optional icon and change indicators; supports an enhanced analytics mode with gradients, percentage and tooltip support.
 * @component
 * @selector app-stats-card
 * @example
 *   <app-stats-card
 *     [title]="'Revenue'"
 *     [value]="12500"
 *     [color]="'green'"
 *     [icon]="'trending-up'"
 *     [enhancedMode]="true"
 *     [percentage]="'12%'"
 *     [tooltip]="'Revenue vs previous period'">
 *   </app-stats-card>
 * @input {string} title - Primary heading or label for the stat card.
 * @input {string|number} value - Main numeric or string value displayed prominently.
 * @input {string} color - Theme color used for the card (defaults to 'green'); affects built-in classes.
 * @input {string=} valueColor - Optional color applied specifically to the value text (also set as CSS variable --value-color).
 * @input {string=} valueFontSize - Optional font-size for the value (e.g. '20px', '1.25rem').
 * @input {string=} change - Optional change text (e.g. '+5%', '-2.3').
 * @input {'positive'|'negative'|'neutral'=} changeType - Semantic indicator for change styling.
 * @input {string=} customClass - Additional CSS class(es) appended to the root card element.
 * @input {string=} icon - Icon identifier or name to render alongside the title/value.
 * @input {string=} iconClass - CSS class applied to the icon element.
 * @input {any=} iconStyle - Inline style object applied to the icon element.
 * @input {string=} percentage - Supplemental percentage text used in enhanced analytics mode.
 * @input {string=} periodText - Label describing the period for the metric (e.g. 'vs last month').
 * @input {string=} tooltip - Optional tooltip text shown on hover or click (manual tooltip handling).
 * @input {string=} gradient - CSS gradient string used as the background when enhancedMode is true.
 * @input {boolean} enhancedMode - When true the component renders in analytics/enhanced mode (adds classes and supports gradient/percentage).
 * @output none - This component does not emit Angular Output events; interactions are handled internally (tooltip display uses direct DOM manipulation).
 *
 * Key methods:
 *  - getCardClass(): string
 *      Returns a space-separated CSS class list for the root card element based on color, customClass and enhancedMode.
 *  - getCardStyle(): any
 *      Returns inline styles for the card (applies provided gradient when enhancedMode is enabled).
 *  - getValueStyle(): any
 *      Builds inline style object for the value element using valueColor and valueFontSize (also sets a CSS variable).
 *  - isTouch (getter): boolean
 *      Convenience getter that exposes whether the current device is a touch device (relies on isMobileOrTablet).
 *  - isMobileOrTablet (getter): boolean
 *      Detects mobile/tablet environment using the helper getIsMobileOrTablet() (returns false if window is undefined).
 *  - onTooltipClick(event, tooltipText): void
 *      Handles manual click-to-show tooltip behavior: prevents default, cleans up existing tooltips and shows a click-tooltip that auto-removes after ~3s and listens for outside clicks.
 *  - onTooltipHover(event, tooltipText): void
 *      Handles hover-to-show tooltip behavior: cleans up existing tooltips and shows a hover tooltip that is removed on mouse leave.
 *  - onTooltipLeave(event): void
 *      Hides/removes any manual tooltips (called on mouseleave to support hover tooltips).
 *  - cleanupTooltips(): void (private)
 *      Removes any existing tooltip DOM elements matching known tooltip selectors to avoid duplicates.
 *  - showManualTooltip(target, text, isHover = false): void (private)
 *      Creates and positions a custom tooltip element in the DOM, styles it, constrains it within viewport paddings, auto-removes for click-tooltips and registers temporary outside-click listeners to dismiss.
 *
 * Important notes / lifecycle:
 *  - This standalone component imports CommonModule and relies on an external helper getIsMobileOrTablet() for device detection.
 *  - There are no explicit Angular lifecycle hooks implemented (e.g. ngOnInit/ngOnDestroy). Tooltip DOM nodes are created and removed manually; click listeners for dismissal are attached temporarily and removed when the tooltip is removed. Consumers should be aware of this behavior if integrating with other global DOM listeners or server-side rendering contexts.
 */
@Component({
  selector: 'app-stats-card',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './stats-card.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./stats-card.component.scss'],
})
export class StatsCardComponent {
  @Input() title = '';
  @Input() value: string | number = '';
  @Input() color = 'green';
  @Input() valueColor?: string; // New input for value-specific color
  @Input() valueFontSize?: string;
  @Input() change?: string;
  @Input() changeType?: 'positive' | 'negative' | 'neutral';
  @Input() customClass?: string;
  @Input() icon?: string;
  @Input() iconClass?: string;
  @Input() iconStyle?: any;

  // Enhanced mode inputs (for Analytics)
  @Input() percentage?: string;
  @Input() periodText?: string;
  @Input() tooltip?: string;
  @Input() gradient?: string;
  @Input() enhancedMode = false;

  /**
   * Builds and returns the CSS class string for the stats card component based on instance properties.
   * @example
   * etCardClass()
   * "stat-card stat-primary my-class enhanced-mode"
   * @returns {{string}} Final space-separated CSS class list for the card.
   **/
  getCardClass(): string {
    const classes = ['stat-card', `stat-${this.color}`];

    if (this.customClass) {
      classes.push(this.customClass);
    }

    if (this.enhancedMode) {
      classes.push('enhanced-mode');
    }

    return classes.join(' ');
  }

  getCardStyle(): any {
    if (this.enhancedMode && this.gradient) {
      return {
        background: this.gradient,
      };
    }
    return {};
  }

  /**
   * Generates an inline style object for the value element, applying value color and font-size when available.
   * @example
   * etValueStyle()
   * // { "--value-color": "#ff0000", "color": "#ff0000", "font-size": "16px" }
   * @returns {{any}} Inline style object containing optional --value-color, color, and font-size properties.
   **/
  getValueStyle(): any {
    const style: any = {};

    if (this.valueColor) {
      style['--value-color'] = this.valueColor;
      style['color'] = this.valueColor;
    }

    if (this.valueFontSize) {
      style['font-size'] = this.valueFontSize;
    }

    return style;
  }

  // Mobile detection for tooltip events
  get isTouch(): boolean {
    return this.isMobileOrTablet;
  }

  get isMobileOrTablet(): boolean {
    if (typeof window === 'undefined') return false;
    return getIsMobileOrTablet();
  }

  // Manual tooltip handling for all devices
  /**
   * Handle a click event to clean up existing tooltips and show a manual tooltip for the clicked element.
   * @example
   * nTooltipClick(event, 'More info')
   * undefined
   * @param {{Event}} {{event}} - Event object from the click that triggered the tooltip.
   * @param {{string}} {{tooltipText}} - Text to display inside the tooltip.
   * @returns {{void}} Nothing; performs UI updates to display the tooltip.
   **/
  onTooltipClick(event: Event, tooltipText: string): void {
    event.preventDefault();
    event.stopPropagation();

    // Clean up any existing tooltips
    this.cleanupTooltips();

    // Ensure target is valid
    const target = event.target as HTMLElement;
    if (!target) {
      return;
    }

    // Create and show tooltip manually (click tooltip)
    this.showManualTooltip(target, tooltipText, false);
  }

  // Hover tooltip handling for desktop
  /**
   * Display a hover tooltip for the event target after cleaning up any existing tooltips.
   * @example
   * nTooltipHover(new Event('mouseover'), 'Details')
   * undefined
   * @param {Event} event - The DOM event that triggered the hover action.
   * @param {string} tooltipText - The text to display inside the tooltip.
   * @returns {void} No return value.
   **/
  onTooltipHover(event: Event, tooltipText: string): void {
    // Clean up any existing tooltips
    this.cleanupTooltips();

    // Ensure target is valid
    const target = event.target as HTMLElement;
    if (!target) {
      return;
    }

    // Create and show tooltip manually (hover tooltip)
    this.showManualTooltip(target, tooltipText, true);
  }

  // Hide tooltip on mouse leave
  onTooltipLeave(event: Event): void {
    // Hide tooltip on mouse leave for all devices
    this.cleanupTooltips();
  }

  // Clean up tooltips helper method
  private cleanupTooltips(): void {
    // Remove all existing tooltips from the DOM
    const existingTooltips = document.querySelectorAll('.p-tooltip, .custom-tooltip');
    existingTooltips.forEach(tooltip => {
      tooltip.remove();
    });
  }

  /**
   * Show a manually triggered tooltip anchored to a target element; supports hover and click variants.
   * @example
   * showManualTooltip(targetElement, 'Tooltip content', false)
   * undefined
   * @param {{HTMLElement}} {{target}} - DOM element to which the tooltip will be anchored.
   * @param {{string}} {{text}} - Text content to display inside the tooltip.
   * @param {{boolean}} {{isHover}} - If true, treats the tooltip as a hover tooltip (no auto-dismiss or outside-click removal).
   * @returns {{void}} Return description in one line.
   **/
  private showManualTooltip(target: HTMLElement, text: string, isHover = false): void {
    // Validate target exists and is in the DOM
    if (!target || !document.body.contains(target)) {
      return;
    }

    // Clean up any existing tooltips first
    this.cleanupTooltips();

    // Create tooltip element
    const tooltip = document.createElement('div');
    tooltip.className = 'p-tooltip p-component p-tooltip-top custom-tooltip';
    tooltip.style.position = 'fixed';
    tooltip.style.zIndex = '10000';
    tooltip.style.pointerEvents = 'none';

    const tooltipText = document.createElement('div');
    tooltipText.className = 'p-tooltip-text';
    tooltipText.textContent = text;
    tooltipText.style.background = 'rgba(0, 0, 0, 0.9)';
    tooltipText.style.color = '#ffffff';
    tooltipText.style.border = '1px solid #E5E7EB';
    tooltipText.style.borderRadius = '8px';
    tooltipText.style.padding = '12px 16px';
    tooltipText.style.fontSize = '14px';
    tooltipText.style.fontFamily = 'Inter, sans-serif';
    tooltipText.style.maxWidth = '280px';
    tooltipText.style.wordWrap = 'break-word';
    tooltipText.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';

    tooltip.appendChild(tooltipText);
    document.body.appendChild(tooltip);

    // Position tooltip
    const rect = target.getBoundingClientRect();
    const tooltipRect = tooltip.getBoundingClientRect();

    const left = rect.left + rect.width / 2 - tooltipRect.width / 2;
    const top = rect.top - tooltipRect.height - 8;

    tooltip.style.left = `${Math.max(16, Math.min(left, window.innerWidth - tooltipRect.width - 16))}px`;
    tooltip.style.top = `${Math.max(16, top)}px`;

    // Only auto-remove after 3 seconds for click tooltips, not hover tooltips
    if (!isHover) {
      setTimeout(() => {
        if (tooltip.parentNode) {
          tooltip.remove();
        }
      }, 3000);
    }

    // Remove on click outside (only for click tooltips)
    if (!isHover) {
      const removeTooltip = (e: Event) => {
        if (!tooltip.contains(e.target as Node) && !target.contains(e.target as Node)) {
          tooltip.remove();
          document.removeEventListener('click', removeTooltip);
          document.removeEventListener('touchstart', removeTooltip);
        }
      };

      setTimeout(() => {
        document.addEventListener('click', removeTooltip);
        document.addEventListener('touchstart', removeTooltip);
      }, 100);
    }
  }
}
